import React, {useContext, useEffect, useRef, useState} from "react";
import useConsumer from "../channels/consumer";
import {useAssetsDispatch} from "../contexts/AssetsContext";
import {useNotificationsDispatch} from "../contexts/NotificationsContext";
import {useBulkJobsDispatch} from "../contexts/BulkJobsContext";
import {notification, message} from "antd";
import User from "./helpers/User";
import {useTranslation} from "react-i18next";
import {useLinksDispatch} from "../contexts/LinksContext";
import {AppContext} from "../contexts/AppContext";
import {useEditAssetsDispatch} from "../contexts/EditAssetsContext";
import {useAssetGroupDispatch, useAssetGroupState} from "../contexts/AssetGroupContext";
import {SessionContext} from "../contexts/SessionContext";
import {useLoadedAssetsDispatch} from "../contexts/LoadedAssetsContext";
import {useUploadsDispatch} from "../contexts/UploadsContext";
import {useAggsDispatch} from "../contexts/AggsContext";
import api from "@/components/api";
import {createDataRefreshCoordinator} from "./helpers/dataRefreshCoordinator";
import {updateAssetForCurrentProcessingContext} from "./helpers/processingProgressContext";
import {dispatchScopedUserCableEvent} from "./helpers/userCableEventOrganization";
import {useBackgroundActivities} from "../contexts/BackgroundActivitiesContext";
import {currentAssetGroupRefreshId} from "./helpers/assetGroupFind";
import {waitForCurrentAssetGroupRefresh} from "./helpers/currentAssetGroupRefresh";
import BulkJobMonitor from './BulkJobMonitor';
import UploadBatchMonitor from './uploads/UploadBatchMonitor';
import {currentUploadBatchStorageKey} from './uploads/currentUploadBatches';

let currentAssetGroupRefreshSequence = 0;

export default ({}) => {
    console.log('🔴 ActionCable component mounting/updating at', new Date().toISOString());
    const {state, dispatch} = useContext(AppContext);
    const {currentUser} = state;

    const {state: sessionState} = useContext(SessionContext);
    const {currentOrg} = sessionState;

    const {t} = useTranslation();
    const assetsDispatch = useAssetsDispatch();
    const notificationsDispatch = useNotificationsDispatch();
    const bulkJobDispatch = useBulkJobsDispatch()
    const linksDispatch = useLinksDispatch()
    const editAssetsDispatch = useEditAssetsDispatch()
    const loadedAssetsDispatch = useLoadedAssetsDispatch()
    const uploadsDispatch = useUploadsDispatch()
    const aggsDispatch = useAggsDispatch()
    const assetGroupDispatch = useAssetGroupDispatch()
    const {receiveCableEvent, refresh: refreshBackgroundActivities} = useBackgroundActivities()
    const receiveCableEventRef = useRef(receiveCableEvent)
    receiveCableEventRef.current = receiveCableEvent
    const refreshBackgroundActivitiesRef = useRef(refreshBackgroundActivities)
    refreshBackgroundActivitiesRef.current = refreshBackgroundActivities

    const {currentAssetGroup, currentUpload} = useAssetGroupState();
    const currentOrgId = useRef(currentOrg?.id)
    currentOrgId.current = currentOrg?.id

    // Use a ref here to ensure it's current in the ActionCable context::
    const currentAssetGroupId = useRef()
    currentAssetGroupId.current = currentAssetGroup?.asset_group_id  || currentAssetGroup?.id

    const currentAssetGroupRefreshTargetId = useRef()
    currentAssetGroupRefreshTargetId.current = currentAssetGroupRefreshId(currentAssetGroup)

    const currentUploadId = useRef()
    currentUploadId.current = currentUpload?.id

    const consumer = useConsumer();
    const userSub = useRef();
    const appSub = useRef();
    const orgSub = useRef();
    const assetIndexSub = useRef();
    const refreshCoordinator = useRef();

    if (!refreshCoordinator.current) {
        refreshCoordinator.current = createDataRefreshCoordinator({
            refresh: scopes => {
                const has = scope => scopes.includes(scope)
                if (has('upload_status')) editAssetsDispatch({type: 'increment'})

                const groupScopes = [
                    ['collections', 'reloadCollections'],
                    ['lightboxes', 'reloadLightboxes'],
                    ['storage_folders', 'reloadStorageFolders'],
                    ['collection_shares', 'reloadCollectionShares'],
                ]
                const matchedGroupScopes = groupScopes.filter(([scope]) => has(scope))
                matchedGroupScopes.forEach(([, type]) => assetGroupDispatch({type}))
                if (has('asset_groups') && !matchedGroupScopes.length) assetGroupDispatch({type: 'reloadAll'})

                const pendingRefreshes = []
                const coveredScopes = new Set()
                const detail = {
                    scopes,
                    waitUntil: (promise, handledScopes = []) => {
                        pendingRefreshes.push(Promise.resolve(promise).then(result => {
                            if (!result?.disposed && result?.verified !== false) {
                                handledScopes.forEach(scope => coveredScopes.add(scope))
                            }
                            return result
                        }))
                    },
                }
                window.dispatchEvent(new CustomEvent('mediagraph:dataRefresh', {detail}))
                if (has('activities')) {
                    coveredScopes.add('activities')
                    pendingRefreshes.push(refreshBackgroundActivitiesRef.current())
                }
                return Promise.all(pendingRefreshes).then(results => ({
                    results,
                    coveredScopes: Array.from(coveredScopes),
                    verified: pendingRefreshes.length > 0 && scopes.every(scope => coveredScopes.has(scope)),
                }))
            },
            refreshCurrentAssetGroup: () => {
                if (!currentAssetGroupRefreshTargetId.current) return Promise.resolve();
                const refreshRequestId = `${currentOrgId.current || 'none'}:${++currentAssetGroupRefreshSequence}`;
                const refreshCompleted = waitForCurrentAssetGroupRefresh(refreshRequestId);
                assetGroupDispatch({
                    type: 'reloadCurrentAssetGroup',
                    assetGroupId: currentAssetGroupRefreshTargetId.current,
                    refreshRequestId,
                });
                return refreshCompleted;
            },
            getStatusContext: () => ({organizationId: currentOrgId.current}),
            onStatusChange: status => {
                window.dispatchEvent(new CustomEvent('mediagraph:refreshStatus', {
                    detail: status,
                }))
            },
        })
    }

    const disconnectErrorNotification = useRef()

    useEffect(()=> {
        console.log('ActionCable useEffect:', { currentUser: !!currentUser, consumer: !!consumer });
        if(!consumer) {
            console.log('ActionCable: No consumer available');
            return;
        }
        
        // Ensure connection is open before creating subscriptions
        if (!consumer.connection.isActive()) {
            console.log('ActionCable: Connection not active, opening...');
            consumer.connection.open();
        }
        
        // Allow subscriptions even without currentUser for public/guest access
        if(!currentUser) {
            console.log('ActionCable: No currentUser, but creating subscriptions anyway');
        }

        // Clear existing subscriptions if they exist to force recreation
        if (userSub.current) {
            console.log('Clearing existing UserChannel subscription');
            userSub.current.unsubscribe();
            userSub.current = null;
        }

        if(!userSub.current && consumer.subscriptions) {
            console.log('Creating UserChannel subscription...', {
                subscriptionsCount: consumer.subscriptions.subscriptions.length,
                connectionState: consumer.connection.getState(),
                isActive: consumer.connection.isActive()
            });
            userSub.current = consumer.subscriptions.create({channel: "UserChannel"}, {
                connected: ()=> {
                    console.log('UserChannel connected - actually receiving data now', {
                        subscriptionsCount: consumer.subscriptions.subscriptions.length
                    });
                    bulkJobDispatch({type: 'setSubscriptionReady', ready: true});
                },

                disconnected: ()=> {
                    console.log('UserChannel disconnected')
                    bulkJobDispatch({type: 'setSubscriptionReady', ready: false});
                },

                rejected: ()=> {
                    console.log('UserChannel rejected, logged out!')
                    bulkJobDispatch({type: 'setSubscriptionReady', ready: false});
                },

                received: (data) => {
                    console.log('UserChannel received', data)
                    dispatchScopedUserCableEvent({
                        data,
                        currentOrganizationId: currentOrg.id,
                        receiveCableEvent: receiveCableEventRef.current,
                        invalidate: refreshCoordinator.current.invalidate,
                    })

                    switch(data.type) {
                        case 'processingProgress':
                            const currentProcessingContext = data.attrs.upload_id === currentUploadId.current || data.attrs.storage_folder_id === currentAssetGroupId.current

                            if(currentProcessingContext) {
                                if(data.attrs.processing_error) {
                                    const description = (
                                        <>
                                            {data.attrs.filename} was not able to process, please check the file and upload again.
                                            <br/>
                                            {data.attrs.lightroom_publish_service_id && (
                                                <>
                                                    <a href={`lightroom://${Config.lightroomProtocol}/?action=uploadvalidation&organization_id=${currentOrg.id}&storage_folder_id=${data.attrs.storage_folder_id}&lightroom_publish_service_id=${data.attrs.lightroom_publish_service_id}`}>
                                                        Click Here
                                                    </a>
                                                    &nbsp;to retry upload in Lightroom.
                                                </>
                                            )}
                                        </>
                                    )

                                    notification.error({
                                        description,
                                        message: 'Asset Processing Error',
                                        duration: 0
                                    })
                                }

                                return updateAssetForCurrentProcessingContext({
                                    data,
                                    currentUploadId: currentUploadId.current,
                                    currentAssetGroupId: currentAssetGroupId.current,
                                    assetsDispatch,
                                })
                            }
                            return

                        case 'transcribeProgress':
                            return assetsDispatch({type:'updateAsset', asset: {id: data.asset_id, ...data.attrs}});

                        case 'addTags':
                            return assetsDispatch({type:'addTags', tags: data.tags});

                        case 'notification':
                            return notificationsDispatch({type: 'newNotification', notification: data})

                        case 'error':
                            notification.error({
                                description: data.description,
                                message: `Error: ${data.message}`,
                                duration: 0
                            })
                            return

                        case 'bulkJobProgress':
                            bulkJobDispatch({...data, type:'progress'})
                            // Show warning notifications for bulk job issues
                            if (data.done && data.warnings?.length) {
                                const caiWarnings = data.warnings.filter(w => w.code === 'cai_budget_exceeded');
                                const permissionWarnings = data.warnings.filter(w => w.code === 'cai_permission_denied');
                                const otherWarnings = data.warnings.filter(w => !['cai_budget_exceeded', 'cai_permission_denied'].includes(w.code));

                                if (caiWarnings.length) {
                                    notification.warning({
                                        message: t('composable-ai-budget-exceeded-notification', 'Composable AI Budget Exceeded'),
                                        description: t('composable-ai-budget-exceeded-notification-description', 'AI generation was skipped for {{count}} asset(s) because the Composable AI budget was exceeded.', {count: caiWarnings.length}),
                                        duration: 0
                                    });
                                }
                                if (permissionWarnings.length) {
                                    notification.warning({
                                        message: t('cai-permission-denied', 'AI Permission Denied'),
                                        description: t('cai-permission-denied-description', 'AI generation was skipped for {{count}} asset(s) due to insufficient permissions.', {count: permissionWarnings.length}),
                                        duration: 0
                                    });
                                }
                                if (otherWarnings.length) {
                                    notification.warning({
                                        message: t('bulk-job-warnings', 'Bulk Job Warnings'),
                                        description: t('bulk-job-warnings-description', '{{count}} asset(s) had warnings during processing.', {count: otherWarnings.length}),
                                        duration: 0
                                    });
                                }
                            }
                            return

                        case 'linkSpidered':
                            return linksDispatch({type:'spidered', id: data.id})

                        case 'dataDownload':
                            return bulkJobDispatch({data, type:'dataDownload'})

                        case 'metaDownloadProgress':
                            return bulkJobDispatch({data, type:'metaDownloadProgress'})

                        case 'lightroomConnected':
                            message.success('Lightroom Connected!')
                            notification.close('lightroomConnectionNotification')
                            return

                        case 'ingestionAssetAdded':
                            const {asset} = data;
                            if(asset.upload_id === currentUploadId.current || asset.storage_folder_id === currentAssetGroupId.current){
                                assetsDispatch({type: 'assetsAdded', assets: [asset]});
                                loadedAssetsDispatch({type: 'assetsAdded', assets: [asset]});
                                uploadsDispatch({type:'addUploadingAssets', assets: [{...asset, aasm_state: 'pending'}]})
                                aggsDispatch({type:'incrementTotal'})
                            }
                            return

                        case 'assetGroupAssetAdded':
                            console.log('assetGroupAssetAdded', data.asset_group_id, currentAssetGroupId.current)
                            if (data.asset_group_id === currentAssetGroupId.current) {
                                api(`/api/assets/${data.asset_id}`).then(res => {
                                    const asset = res.data
                                    assetsDispatch({type: 'assetsAdded', assets: [asset]});
                                    loadedAssetsDispatch({type: 'assetsAdded', assets: [asset]});
                                    uploadsDispatch({type:'addUploadingAssets', assets: [{...asset, aasm_state: 'pending'}]})
                                    aggsDispatch({type:'incrementTotal'})
                                })
                            }
                            return
                    }
                }
            });
        }

        // Clear existing AppChannel subscription if it exists
        if (appSub.current) {
            console.log('Clearing existing AppChannel subscription');
            appSub.current.unsubscribe();
            appSub.current = null;
        }

        if(!appSub.current && consumer.subscriptions) {
            appSub.current = consumer.subscriptions.create({channel: "AppChannel"}, {
                connected: ()=> {
                    console.log(`AppChannel connected @ ${new Date()}`);
                    if(disconnectErrorNotification.current) {
                        disconnectErrorNotification.current = false
                        uploadsDispatch({type:'reconnected'})
                        refreshCoordinator.current.reconnect()
                    }
                },

                disconnected: ()=> {
                    console.log(`AppChannel disconnected @ ${new Date()}`)
                    disconnectErrorNotification.current = true
                    uploadsDispatch({type:'disconnected'})
                },

                rejected: ()=> {
                    console.log('AppChannel rejected, logged out!')
                },

                received: (data) => {
                    console.log('AppChannel received', data)

                    switch(data.type) {
                        case 'reindexing':
                            return notification.warn({
                                message: 'Search Re-Indexing starting...',
                                description: 'Your Assets will begin reindexing into the search system shortly, so you may not see all of them when browsing and searching. Refresh the page to see your estimated completion time.',
                                duration: 0
                            })
                        case 'newVersion':
                            return notification.warn({
                                message: 'New App Version Released',
                                description: 'Please refresh the page to load the new app version.',
                                duration: 0
                            })
                        case 'forceReload':
                            setTimeout(()=>{
                                window.location.reload()
                            }, 30 * 1000)

                            return notification.warn({
                                message: 'New Major App Version Released',
                                description: 'Your browser will be reloaded in 30 seconds to update the application.',
                                duration: 30
                            })
                    }
                }
            });
        }

        // Clear existing OrganizationChannel subscription if it exists
        if (orgSub.current) {
            console.log('Clearing existing OrganizationChannel subscription');
            orgSub.current.unsubscribe();
            orgSub.current = null;
        }

        if(!orgSub.current && consumer.subscriptions) {
            orgSub.current = consumer.subscriptions.create({channel: "OrganizationChannel"}, {
                connected: ()=> {
                    console.log(`OrganizationChannel connected @ ${new Date()}`);
                },

                disconnected: ()=> {
                    console.log(`OrganizationChannel disconnected @ ${new Date()}`)
                },

                rejected: ()=> {
                    console.log('OrganizationChannel rejected, logged out!')
                },

                received: (data) => {
                    console.log('OrganizationChannel received', data)
                    refreshCoordinator.current.invalidate(data)

                    switch(data.type) {
                        case 'treeChanged':
                            break;
                        case 'dataInvalidated':
                            break;
                        case 'caiBudgetUpdated':
                            console.log('caiBudgetUpdated', data)
                            window.dispatchEvent(new CustomEvent('caiBudgetUpdated', {detail: data}));
                            break;
                    }
                }
            });
        }

        if (assetIndexSub.current) {
            assetIndexSub.current.unsubscribe();
            assetIndexSub.current = null;
        }

        if(!assetIndexSub.current && consumer.subscriptions && currentOrg?.id) {
            assetIndexSub.current = consumer.subscriptions.create(
                {channel: "AssetIndexChannel", organization_id: currentOrg.id},
                {
                    received: data => refreshCoordinator.current.invalidate({...data, type: 'assetIndexCompleted'}),
                }
            );
        }

        // Verify subscriptions were created
        setTimeout(() => {
            if (consumer && consumer.subscriptions) {
                const subs = consumer.subscriptions.subscriptions;
                console.log('ActionCable subscription verification:', {
                    totalSubscriptions: subs.length,
                    userSub: !!userSub.current,
                    appSub: !!appSub.current,
                    orgSub: !!orgSub.current,
                    connectionActive: consumer.connection.isActive(),
                    subscriptionDetails: subs.map(s => ({
                        identifier: s.identifier,
                        consumer: !!s.consumer
                    }))
                });
                
                // Force connection if subscriptions exist but connection is inactive
                if (subs.length > 0 && !consumer.connection.isActive()) {
                    console.log('Force opening connection for subscriptions');
                    consumer.connection.open();
                }
            }
        }, 1000);

        return ()=> {
            userSub.current?.unsubscribe()
            appSub.current?.unsubscribe()
            orgSub.current?.unsubscribe()
            assetIndexSub.current?.unsubscribe()
        }

    }, [currentUser?.id, currentOrg?.id, consumer])

    useEffect(() => () => refreshCoordinator.current?.dispose(), [])

    useEffect(() => {
        const retryRefresh = () => refreshCoordinator.current?.reconnect()
        window.addEventListener('mediagraph:retryRefresh', retryRefresh)
        return () => window.removeEventListener('mediagraph:retryRefresh', retryRefresh)
    }, [])

    return <>
        <BulkJobMonitor
            organizationId={currentOrg?.id}
            userId={currentUser?.id}
            onTerminal={data => refreshCoordinator.current.invalidate(data)}
        />
        <UploadBatchMonitor
            scope={currentOrg?.id && currentUser?.id ? currentUploadBatchStorageKey(currentOrg.id, currentUser.id) : null}
            onSettled={data => refreshCoordinator.current.invalidate(data)}
        />
    </>;
}
